/**
 * Site-wide nudge shown to a logged-in user whose email is still unverified (only when
 * EMAIL_VERIFICATION_ENABLED). Dismissible, shares the <SiteBanner/> shell with PremiumBanner.
 * Renders nothing when the flag is off, the user is verified/grandfathered, or dismissed - safe to
 * mount app-wide.
 *
 * COPY NOTE (2026-08-10): this used to read "Verify your email to unlock your 5 free premium days"
 * while the premium banner underneath it said "Your premium access has ended" - so the first thing
 * a new account was told was that something they never had was already over. Registering now grants
 * a welcome day outright (see lib/premium/signupGrants.ts) and PremiumBanner stays silent until the
 * email is verified, so this is the ONE bar a new user sees. It leads with what they already have.
 */
'use client'

import { useEffect, useState } from 'react'
import { useSession } from 'next-auth/react'
import { usePathname } from 'next/navigation'
import { Mail } from 'lucide-react'
import SiteBanner from '@/components/SiteBanner'
import { SHOW_VERIFY_PROMPT_EVENT } from '@/components/VerifyEmailPrompt'

export default function VerifyEmailBanner() {
  const pathname = usePathname()
  const { status } = useSession()
  const [needs, setNeeds] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (status !== 'authenticated') return
    let alive = true
    fetch('/api/auth/verification-status')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d) setNeeds(!!d.needsVerification) })
      .catch(() => {})
    return () => { alive = false }
  }, [status])

  if (pathname?.startsWith('/chart/') || pathname === '/alerts' || pathname === '/stream') return null
  if (status !== 'authenticated' || !needs || dismissed) return null

  return (
    <SiteBanner
      tone="amber"
      Icon={Mail}
      title="Verify your email to add 5 more premium days"
      message="You're on your welcome day of full access. Verifying adds 5 more, no card needed."
      // Re-opens <VerifyEmailPrompt/> instead of firing a bare resend. A resend cannot help the
      // common cases (it is in Promotions, the address has a typo, the 24h token expired) and this
      // bar has no room to explain any of them, so it hands off to the card that does. One place to
      // act, one place to keep correct.
      cta={{ label: 'Show me how', onClick: () => window.dispatchEvent(new Event(SHOW_VERIFY_PROMPT_EVENT)) }}
      onDismiss={() => setDismissed(true)}
    />
  )
}
